import React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row } from "react-bootstrap";
import VehicleComponent from "@/shared/components/table/components/VehicleComponent";
import RowOptions from "@/shared/components/table/components/RowOptions";
import DataReactTable from "../../DataTable/components/DataReactTable";
import {
  LastServiceComponent,
  StatusComponent,
} from "../MaintenanceReminders/MaintenanceRemindersTable";
import {
  AddFormButton,
  ColumnHeading,
} from "../../../shared/components/TableElements";
import { Link } from "react-router-dom";
import { NATEXT } from "../../../shared/constants";
import { useState } from "react";
import getUnixTime from "date-fns/getUnixTime";
import ExplorePopup from "../../../shared/components/ExplorePopup";
import { LinkSpan } from "../../../shared/components/form/FormElements";
import useFetchData from "../../../shared/custom-hooks/useFetchData";
import {
  getDistanceUnit,
  getListingDisplayDate,
} from "../../../shared/helpers";
import getDefaultFilterData from "../../../shared/constants/getDefaultFilterData";
import { colorGray } from "../../../utils/palette";
import { useAuth } from "../../../contexts/AuthContext";

const RenewalsTable = () => {
  const { t } = useTranslation("common");
  const { isViewOnly } = useAuth();

  const { minDate } = useSelector((state) => state.columns);

  const rowData = useSelector((state) => state.filter.rowData);

  const [showExplorePopup, setShowExplorePopup] = useState(false);

  useFetchData({
    startDate: 0,
    endDate: getUnixTime(new Date()),
    query: "get_all_expense_reminder_data_v2",
    sortIndex: 3,
  });

  const { defaultContainsData, defaultRangeData, defaultStatusData } =
    getDefaultFilterData();

  const initialData = useMemo(
    () => ({
      // status: {
      //   column: "status",
      //   name: t("status"),
      //   ...initialStatusData,
      // },
      task: {
        column: "task",
        name: t("task"),
        ...defaultContainsData,
      },
      daysRemaining: {
        column: "daysRemaining",
        name: t("days_remaining"),
        ...defaultRangeData,
      },
      odoRemaining: {
        column: "odoRemaining",
        name: t("odo_remaining"),
        ...defaultRangeData,
      },
      lastServiceOdo: {
        column: "lastServiceOdo",
        name: t("last_expense_odo"),
        ...defaultRangeData,
      },
      lastServiceDate: {
        column: "lastServiceDate",
        name: t("last_expense_date"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
        mode: "custom",
      },
      dueOdo: {
        column: "dueOdo",
        name: t("due_odo"),
        ...defaultRangeData,
      },
      dueDate: {
        column: "dueDate",
        name: t("due_date"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
        mode: "custom",
      },
    }),
    [minDate],
  );

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("vehicle"),
        accessor: "name",
        Cell: ({ row }) => (
          <VehicleComponent
            vehicleName={row.original.NAME}
            //groupName="N/A"
            vehicleId={row.original.veh_id}
            vehicleImg={row.original.veh_img}
            orgId={row.original.org_id}
          />
        ),
      },
      {
        id: 2,
        Header: t("task"),
        accessor: "expense_task_name",
        accessorId: "task_name",
        Cell: ({ row }) => {
          const { expense_task_name, recurring } = row.original;
          return (
            <div className="d-flex align-items-center gap-1">
              <p>{expense_task_name}</p>
              {recurring === 0 && (
                <>
                  <p style={{ fontSize: "8px", color: colorGray }}>{"●"}</p>
                  <p style={{ color: colorGray }}>{t("one_time")}</p>
                </>
              )}
            </div>
          );
        },
      },
      {
        id: 3,
        Header: t("status"),
        accessor: "status",
        Cell: ({ row }) => {
          const { due_days, last_date_of_service } = row.original;
          let due = last_date_of_service + Number(due_days) * 86400;
          let currentDate = Math.floor(Date.now() / 1000);
          let dueIn = ((due - currentDate) / (60 * 60 * 24)).toFixed(0);
          return (
            <StatusComponent
              due_days={row.original.due_days}
              due_odo={row.original.due_odo}
              distance={row.original["DUE ODO"] - row.original["MAX ODO"]}
              percentage={row.original.PERCENTAGE}
              days={dueIn}
              rowData={row.original}
            />
          );
        },
      },
      // {
      //   id: 4,
      //   Header: t("due_on"),
      //   accessor: "due",
      //   disableSortBy: true,
      //   Cell: ({ row }) => {
      //     let dueDate = new Date(row.original.last_date_of_service * 1000);
      //     const dueDays = +row.original.due_days;
      //     let lastServiceDate = new Date(
      //       row.original.last_date_of_service * 1000
      //     );
      //     dueDate.setDate(lastServiceDate.getDate() + dueDays);
      //     dueDate =
      //       dueDate && dueDate instanceof Date && !isNaN(dueDate)
      //         ? getListingDisplayDate(dueDate / 1000)
      //         : NATEXT;
      //     return (
      //       <DueComponent
      //         distance={row.original["DUE ODO"]}
      //         date={dueDate}
      //         due_days={row.original.due_days}
      //         due_odo={row.original.due_odo}
      //         rowData={row.original}
      //       />
      //     );
      //   },
      // },
      {
        id: 4,
        Header: `${t("due_on_odo")}`,
        accessor: "DUE ODO",
        //disableSortBy: true,
        Cell: ({ row }) => {
          const dueOdo = row.original["DUE ODO"];
          const { primary_meter, due_odo } = row.original;
          if (+dueOdo <= 0 || +due_odo <= 0) {
            return NATEXT;
          }
          let distanceUnit = useSelector((state) => state.units.distance);
          if (primary_meter === "Hours") {
            distanceUnit = "Hours";
          }
          const dueOdoVal = `${dueOdo} ${getDistanceUnit(distanceUnit)}`;
          return dueOdoVal;
        },
      },
      {
        id: 5,
        Header: `${t("due_on_date")}`,
        accessor: "DUE DATE",
        //disableSortBy: true,
        Cell: ({ row }) => {
          const dueDate = row.original["DUE DATE"];
          const { due_days } = row.original;
          if (+due_days <= 0) {
            return NATEXT;
          }
          return dueDate ? getListingDisplayDate(dueDate) : NATEXT;
        },
      },
      {
        id: 6,
        Header: t("last_expense"),
        accessor: "last_service",
        disableSortBy: true,
        Cell: ({ row }) => {
          const dueDate = new Date(row.original.last_date_of_service * 1000);
          const lastServiceDate =
            dueDate && dueDate instanceof Date && !isNaN(dueDate)
              ? getListingDisplayDate(dueDate / 1000)
              : NATEXT;
          return (
            <LastServiceComponent
              distance={row.original.last_odo_of_service}
              date={lastServiceDate}
              rowData={row.original}
            />
          );
        },
      },
      {
        id: "edit",
        Header: "",
        accessor: "edit",
        accessorId: "edit",
        Cell: ({ row }) => <RowOptions rowData={row?.original} />,
        disableSortBy: true,
      },
      // {
      //   id: "edit",
      //   Header: "",
      //   accessor: "edit",
      //   accessorId: "edit",
      //   Cell: () => <RowOptions />,
      //   disableSortBy: true,
      // },
    ],
    [],
  );

  //Default Columns
  let count = 0;
  let defaultColumns = {};
  columnHeaders.forEach((column) => {
    defaultColumns[count] = {
      accessor: column.Header,
      header: column.Header,
    };
    count++;
  });

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };

  return (
    <>
      {showExplorePopup && (
        <ExplorePopup
          showPopup={showExplorePopup}
          setShowPopup={setShowExplorePopup}
          exploreLink={"https://embed.getsmartcue.com/I5TM3PJE"}
        />
      )}
      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around">
          <Col className="d-flex align-items-center gap-2">
            <ColumnHeading>{t("renewals")}</ColumnHeading>

            <LinkSpan
              onClick={() => setShowExplorePopup(true)}
              style={{ alignSelf: "end" }}
            >
              {t("explore")}
            </LinkSpan>
          </Col>
          <Col sm={2} className="d-flex justify-content-end align-items-center">
            {!isViewOnly && (
              <Link to={"/reminders/renewals/addnew"}>
                <AddFormButton>{t("add_renewal")}</AddFormButton>
              </Link>
            )}
          </Col>
        </Row>
        <Row>
          <DataReactTable
            reactTableData={reactTableData}
            //valuesArray={fillupsValuesArray}
            initialData={initialData}
            defaultColumns={defaultColumns}
          />
        </Row>
      </Container>
    </>
  );
};

export default RenewalsTable;
